/**
 * 实时检测混入 (Hot-pluggable Detection Mixin)
 *
 * 使用方式（插上）:
 *   1. import cameraDetector from './cameraDetector.js'
 *   2. 在 export default 中添加 mixins: [cameraDetector]
 *   3. 模板中的动态检测提示会自动激活
 *
 * 移除方式（拔掉）:
 *   删除 import 语句和 mixins 数组项，即可回退到手动拍摄模式。
 *   模板中的 v-if/v-else 链条会自动降级为静态提示文字。
 *
 * 依赖:
 *   - @/utils/tongueDetector  (舌头/面部实时检测)
 *   - @/utils/mediapipeFace   (MediaPipe 人脸检测共享单例)
 *   - public/lib/mediapipe/   (MediaPipe WASM 模型)
 *   - public/lib/ort/         (ONNX Runtime Web)
 *   - public/lib/zhui_an.onnx (EfficientNet-B0 分类模型)
 *   - public/classifierWorker.js (ONNX Worker)
 */

import {
  loadDetector as loadFaceDetector,
  isLoaded as isFaceDetectorLoaded,
  detect as detectFaceFrame,
  STATUS
} from '@/utils/faceDetector';
import {
  loadDetector as loadTongueDetector,
  isLoaded as isTongueDetectorLoaded,
  detect as detectTongueFrame
} from '@/utils/tongueDetector';

var STATUS_NAME = ['NOT_DETECTED', 'TOO_FAR', 'TOO_CLOSE', 'OK'];
var STATUS_TEXT = ['未检测到有效人脸', '距离太远', '距离太近', '检测通过'];

export default {
  data() {
    return {
      // ---- 检测 UI 状态 ----
      detectTip: '',
      detectStatus: STATUS.NOT_DETECTED,
      detectPassed: false,
      tongueModelLoaded: false,
      tongueModelLoading: false,
      faceModelLoaded: false,
      faceModelLoading: false,

      // ---- 内部状态（非响应式习惯，但放 data 里确保初始化） ----
      _detecting: false,
      _autoCaptureDone: false,
      _detectTimer: null,
      _detectCount: 0,
      _passCount: 0,
      _consecutiveErrors: 0,
      _destroyed: false,
      _detectCanvas: null,
      _detectCtx: null,
      _modelInputCanvas: null,
      _modelInputCtx: null,

      // ---- 调试：进模型图片预览 ----
      debugImage: null,
      _debugOverlay: null,
      _debugImg: null,
      _debugLabel: null,
      _debugResultLabel: null,
      _debugCanvas: null,
      _debugCtx: null,

      // ---- 模型状态指示灯 ----
      modelStatus: 0,
      modelDotTimer: null,
      _modelDotEl: null,
    };
  },

  computed: {
    // ---- 检测类型判断 ----
    needModelDetect() {
      return this.needFaceDetect || this.needTongueDetect;
    },
    needTongueDetect() {
      return this.type == 2 || this.type == 3;
    },
    needFaceDetect() {
      return this.type == 1 || this.type == 4 || this.type == 6;
    },

    // ---- 检测环境 ----
    /** 仅本地壳 App 启用实时识别（网页和无舌诊接口的 APP 不做） */
    detectionEnabled() {
      return typeof window !== 'undefined' && !!window.readLocalModel;
    },

    // ---- 模型状态 ----
    activeModelLoaded() {
      if (this.needFaceDetect) return this.faceModelLoaded;
      if (this.needTongueDetect) return this.tongueModelLoaded;
      return false;
    },

    /** 检测通过的期望标签 */
    passLabel() {
      if (this.needFaceDetect) return '嘴';
      if (this.needTongueDetect) return '正常';
      return '';
    },

    /** 模型就绪后的提示语 */
    readyTip() {
      return this.needTongueDetect ? '请伸出舌头对准摄像头' : '保持面部在框定范围内';
    },

    /** 回退（无模型）时的提示语 */
    fallbackTip() {
      return this.needTongueDetect ? '请将舌头靠近摄像头' : '保持面部在框定范围内';
    },

    /** 连续通过次数 */
    requiredPassCount() {
      return 3;
    },

    /** 检测间隔（ms） */
    detectInterval() {
      return 200;
    },

    /** 检测提示的 CSS class */
    detectTipClass() {
      if (this.detectPassed) return 'detect-ok';
      if (this.detectStatus === STATUS.TOO_FAR || this.detectStatus === STATUS.TOO_CLOSE) return 'detect-warn';
      return '';
    },

    // ---- 模型状态指示灯 ----
    modelDotColor() {
      if (this.modelStatus === 1) return '#f90';
      if (this.modelStatus === 2) return '#0f0';
      return '#f00';
    },
    modelDotText() {
      if (this.modelStatus === 1) return '模型加载中...';
      if (this.modelStatus === 2) return '模型就绪';
      return '模型未加载';
    },
  },

  mounted() {
    this._destroyed = false;
    this.initDetection();
    // 仅当需要实时检测时才创建调试浮层
    if (this.needModelDetect) {
      this._createDebugOverlay();
    }
    // 模型状态指示灯（实时检测版本均显示）
    if (this.detectionEnabled) {
      this._createModelDot();
    }
  },

  beforeDestroy() {
    this._destroyed = true;
    this.stopDetectionLoop();
    this._removeDebugOverlay();
    this._removeModelDot();
  },

  methods: {
    // ===================== 对外方法 =====================

    /**
     * 重置检测状态（由 photoFun 调用）
     * 当用户点击"重新检测"时重置所有计数器并重启检测循环
     */
    resetDetection() {
      this.stopDetectionLoop();
      this._autoCaptureDone = false;
      this._detecting = false;
      this._detectCount = 0;
      this._passCount = 0;
      this._consecutiveErrors = 0;
      this.detectStatus = STATUS.NOT_DETECTED;
      this.detectPassed = false;
      this.detectTip = this.activeModelLoaded ? this.readyTip : this.fallbackTip;
      if (this.needModelDetect && this.activeModelLoaded) {
        this.startDetectionLoop();
      }
    },

    // ===================== 初始化 =====================

    /**
     * 初始化实时检测
     * - 检测页面（camera step1/2/3）：加载模型 + 启动检测循环
     * - 非检测页面（device/confirmAssess）：仅预加载模型，指示灯可见
     * - 非实时检测版本：不执行任何操作
     */
    initDetection() {
      // 非实时检测版本（网页版）→ 跳过
      if (!this.detectionEnabled) {
        if (this.needModelDetect) {
          this.detectTip = this.fallbackTip;
        }
        this._hideDebugOverlay();
        return;
      }

      // 如果 main.js 已经预加载完成，进入拍摄页时直接同步模块真实状态。
      if (this.syncLoadedModelState()) {
        if (this.needModelDetect) {
          this.detectTip = this.readyTip;
          this.startDetectionLoop();
        }
        return;
      }

      // 模型尚未加载 → 开始加载（所有页面通用）
      this.setModelState({ loaded: false, loading: true });

      this.loadActiveDetector().then(function () {
        if (this._destroyed) return;
        this.setModelState({ loaded: true, loading: false });

        // 检测页面 → 启动检测循环
        if (this.needModelDetect) {
          this.detectTip = this.readyTip;
          this.startDetectionLoop();
        }
      }.bind(this)).catch(function (e) {
        this.setModelState({ loaded: false, loading: false });
        this._hideDebugOverlay();
        console.error('[Detector] 模型加载失败，已回退到手动拍摄模式');
        console.error('[Detector] 错误详情:', e && e.message || String(e));
        console.error('[Detector] 请检查: 1) /lib/mediapipe/face_detection.js  2) /lib/zhui_an.onnx  3) /classifierWorker.js  4) ONNX WASM');
        if (this.needModelDetect) {
          this.detectTip = this.fallbackTip;
        }
      }.bind(this));
    },

    /**
     * 根据当前拍摄类型加载对应模型。
     * 面诊只需要人脸框和距离判断；舌诊才需要 yolo1 的裁嘴 + 分类。
     * 没有拍摄类型的首页/设备页用于预加载与状态灯：两个模型都完成后才置为就绪。
     */
    loadActiveDetector() {
      if (this.needFaceDetect) return loadFaceDetector();
      if (this.needTongueDetect) return loadTongueDetector();
      return Promise.all([loadFaceDetector(), loadTongueDetector()]);
    },

    /**
     * 从 detector 模块同步真实加载状态，避免预加载完成后新组件仍显示 loading。
     */
    syncLoadedModelState() {
      var faceLoaded = isFaceDetectorLoaded();
      var tongueLoaded = isTongueDetectorLoaded();

      if (this.needFaceDetect) {
        this.setModelState({ loaded: faceLoaded, loading: false });
        return faceLoaded;
      }
      if (this.needTongueDetect) {
        this.setModelState({ loaded: tongueLoaded, loading: false });
        return tongueLoaded;
      }

      if (faceLoaded && tongueLoaded) {
        this.setModelState({ loaded: true, loading: false });
        return true;
      }
      return false;
    },

    /**
     * 根据检测类型设置模型加载状态
     */
    setModelState(state) {
      if (this.needTongueDetect) {
        this.tongueModelLoaded = state.loaded;
        this.tongueModelLoading = state.loading;
      }
      if (this.needFaceDetect) {
        this.faceModelLoaded = state.loaded;
        this.faceModelLoading = state.loading;
      }
      // 写入全局状态供设备页状态指示灯读取：0=未加载/失败, 1=加载中, 2=已加载
      try {
        var status = state.loaded ? 2 : (state.loading ? 1 : 0);
        sessionStorage.setItem('__modelStatus', status);
      } catch (e) { /* sessionStorage 不可用时静默忽略 */ }
    },

    // ===================== 检测循环 =====================

    /**
     * 启动检测循环（节流）
     */
    startDetectionLoop() {
      if (this._detectTimer || this._destroyed || this._autoCaptureDone) return;
      if (!this.needModelDetect || !this.activeModelLoaded) return;
      this.scheduleNextDetect();
    },

    /**
     * 调度下一次检测
     */
    scheduleNextDetect() {
      if (this._destroyed || this._autoCaptureDone) return;

      this._detectTimer = setTimeout(async () => {
        this._detectTimer = null;
        if (this._destroyed || this._autoCaptureDone) return;

        // 上传中或摄像头未就绪则跳过本次
        if (!this.stream || this.loading) {
          this.scheduleNextDetect();
          return;
        }

        await this.detectFrameByYolo1();
        if (!this._autoCaptureDone) {
          this.scheduleNextDetect();
        }
      }, this.detectInterval);
    },

    /**
     * 停止检测循环
     */
    stopDetectionLoop() {
      if (this._detectTimer) {
        clearTimeout(this._detectTimer);
        this._detectTimer = null;
      }
    },

    // ===================== 帧检测 =====================

    /**
     * 获取检测用的视频帧快照
     * 使用独立的离屏 canvas，不干扰预览 canvas
     */
    getDetectSnapshot() {
      var video = this.$refs['video'];
      if (!video || video.readyState < video.HAVE_CURRENT_DATA || !video.videoWidth || !video.videoHeight) {
        return null;
      }
      if (!this._detectCanvas) {
        this._detectCanvas = document.createElement('canvas');
        this._detectCtx = this._detectCanvas.getContext('2d', { willReadFrequently: true });
      }
      if (this._detectCanvas.width !== video.videoWidth || this._detectCanvas.height !== video.videoHeight) {
        this._detectCanvas.width = video.videoWidth;
        this._detectCanvas.height = video.videoHeight;
      }
      this._detectCtx.drawImage(video, 0, 0, this._detectCanvas.width, this._detectCanvas.height);
      return this._detectCanvas;
    },

    /**
     * 生成与页面预览/最终拍照方向一致的模型输入帧。
     * index.vue 的预览是 CSS 旋转，原始 video 帧没有变；这里在进模型前补上真实像素旋转。
     */
    getModelInputSnapshot(snapshot, rotation) {
      if (!snapshot || !snapshot.width || !snapshot.height) return snapshot;
      if (!rotation || rotation === 1) return snapshot;

      if (!this._modelInputCanvas) {
        this._modelInputCanvas = document.createElement('canvas');
        this._modelInputCtx = this._modelInputCanvas.getContext('2d', { willReadFrequently: true });
      }

      var sw = snapshot.width;
      var sh = snapshot.height;
      var dw = (rotation === 2 || rotation === 3) ? sh : sw;
      var dh = (rotation === 2 || rotation === 3) ? sw : sh;

      if (this._modelInputCanvas.width !== dw || this._modelInputCanvas.height !== dh) {
        this._modelInputCanvas.width = dw;
        this._modelInputCanvas.height = dh;
      }

      this._modelInputCtx.clearRect(0, 0, dw, dh);
      this._modelInputCtx.save();
      this._modelInputCtx.translate(dw / 2, dh / 2);
      if (rotation === 2) this._modelInputCtx.rotate(90 * Math.PI / 180);
      else if (rotation === 3) this._modelInputCtx.rotate(-90 * Math.PI / 180);
      else if (rotation === 4) this._modelInputCtx.rotate(180 * Math.PI / 180);
      this._modelInputCtx.drawImage(snapshot, -sw / 2, -sh / 2, sw, sh);
      this._modelInputCtx.restore();

      return this._modelInputCanvas;
    },

    /**
     * 执行一帧 YOLO1 检测
     */
    async detectFrameByYolo1() {
      if (this._detecting) return;
      var snapshot = this.getDetectSnapshot();
      if (!snapshot) return;

      this._detecting = true;
      try {
        // 防御：确保 _detectCount 是有效数字（低端设备可能出现未初始化/被篡改的情况）
        if (typeof this._detectCount !== 'number' || isNaN(this._detectCount)) {
          this._detectCount = 0;
        }
        this._detectCount++;
        // 让出主线程给渲染
        await new Promise(resolve => requestAnimationFrame(resolve));
        // 关键修复：将 rotate90 强制转为整数，避免 sessionStorage 中存储为字符串
        // 导致 tongueDetector/mediapipeFace 中的 === 严格相等判断失败（如 "2" === 2 → false）
        // 而 CSS 使用 == 宽松相等，预览正常但检测未旋转，造成"未检测到人脸"
        var rotation = parseInt(this.rotate90, 10) || 1;
        var modelInput = this.getModelInputSnapshot(snapshot, rotation);
        // 调试：显示真正进模型的图片
        this._updateDebugImage(modelInput, 1, rotation, snapshot);
        // modelInput 已按设备配置旋转到位，传 1 避免底层再次旋转/裁切
        var result = this.needFaceDetect
          ? await detectFaceFrame(modelInput, 1)
          : await detectTongueFrame(modelInput, 1);
        // 调试：显示模型返回的分类分数和距离状态
        this._updateDebugResult(result || {});
        // 检测成功 → 重置连续错误计数
        this._consecutiveErrors = 0;
        this.applyDetectionResult(result || {});
      } catch (e) {
        this._consecutiveErrors++;
        var errMsg = e && e.message || String(e);
        console.error('[Detector] 检测失败 (' + this._consecutiveErrors + '次连续):', errMsg);

        // WASM 内存溢出 或 连续 5 次异常 → 回退到手动拍摄模式
        if (this._consecutiveErrors >= 5 || /memory|access out of bounds/i.test(errMsg)) {
          console.warn('[Detector] 设备性能不足，已回退到手动拍摄模式');
          this.stopDetectionLoop();
          this.setModelState({ loaded: false, loading: false });
          this.detectTip = this.fallbackTip;
          this._hideDebugOverlay();
        }
      } finally {
        this._detecting = false;
      }
    },

    // ===================== 调试：进模型图片预览 =====================

    /**
     * 创建调试浮层，显示送入检测模型的图片
     */
    _createDebugOverlay() {
      if (this._debugOverlay) return;
      var self = this;

      // 浮层容器，默认隐藏，检测启动后才显示
      this._debugOverlay = document.createElement('div');
      this._debugOverlay.style.cssText =
        'display:none;position:absolute;top:8px;right:8px;z-index:999;' +
        'background:rgba(0,0,0,0.75);padding:4px;border-radius:6px;' +
        'width:300px;max-width:38vw;pointer-events:none;';

      // 标题
      var title = document.createElement('div');
      title.style.cssText = 'color:#0f0;font-size:10px;text-align:center;margin-bottom:2px;';
      title.textContent = '实际送入模型的画面';
      this._debugOverlay.appendChild(title);

      // 图片
      this._debugImg = document.createElement('img');
      this._debugImg.style.cssText = 'width:100%;display:block;border-radius:3px;';
      this._debugOverlay.appendChild(this._debugImg);

      // 标签（旋转/尺寸信息）
      this._debugLabel = document.createElement('div');
      this._debugLabel.style.cssText =
        'color:#ff0;font-size:10px;text-align:center;margin-top:2px;' +
        'word-break:break-all;';
      this._debugOverlay.appendChild(this._debugLabel);

      // 结果标签（分类分数/距离状态）
      this._debugResultLabel = document.createElement('div');
      this._debugResultLabel.style.cssText =
        'color:#0ff;font-size:10px;text-align:center;margin-top:2px;' +
        'word-break:break-all;min-height:12px;';
      this._debugOverlay.appendChild(this._debugResultLabel);

      // 延迟挂载，确保 $el 已渲染
      this.$nextTick(function () {
        var container = self.$el.querySelector('.face-container');
        if (container) {
          container.style.position = container.style.position || 'relative';
          container.appendChild(self._debugOverlay);
        }
      });
    },

    /**
     * 移除调试浮层
     */
    _removeDebugOverlay() {
      if (this._debugOverlay && this._debugOverlay.parentNode) {
        this._debugOverlay.parentNode.removeChild(this._debugOverlay);
      }
      this._debugOverlay = null;
      this._debugImg = null;
      this._debugLabel = null;
      this._debugResultLabel = null;
      this._debugCanvas = null;
      this._debugCtx = null;
    },

    /**
     * 隐藏调试浮层（模型未加载或不支持检测时调用）
     */
    _hideDebugOverlay() {
      if (this._debugOverlay) {
        this._debugOverlay.style.display = 'none';
      }
    },

    /**
     * 更新调试预览：将 snapshot 按与 detector 一致的方式旋转后显示
     * @param {HTMLCanvasElement} snapshot - 已处理后的模型输入帧
     * @param {number} rotation - 调试图自身需要追加的旋转值 (1=正常 2=90° 3=-90° 4=180°)
     * @param {number} sourceRotation - 原始设备旋转值，仅用于标签展示
     * @param {HTMLCanvasElement} originalSnapshot - 原始视频帧，仅用于标签展示
     */
    _updateDebugImage(snapshot, rotation, sourceRotation, originalSnapshot) {
      if (!this._debugImg) return;

      // 首次更新时显示浮层
      if (this._debugOverlay && this._debugOverlay.style.display === 'none') {
        this._debugOverlay.style.display = '';
      }

      if (!this._debugCanvas) {
        this._debugCanvas = document.createElement('canvas');
        this._debugCtx = this._debugCanvas.getContext('2d');
      }

      var sw = snapshot.width;
      var sh = snapshot.height;
      var maxSize = 300;
      var rotW, rotH, scale;

      // 与 mediapipeFace.js prepareDetectInput 一致的旋转逻辑
      if (rotation && rotation !== 1) {
        rotW = (rotation === 2 || rotation === 3) ? sh : sw;
        rotH = (rotation === 2 || rotation === 3) ? sw : sh;
        scale = Math.min(1, maxSize / Math.max(rotW, rotH));
        this._debugCanvas.width = Math.round(rotW * scale);
        this._debugCanvas.height = Math.round(rotH * scale);

        this._debugCtx.save();
        this._debugCtx.translate(this._debugCanvas.width / 2, this._debugCanvas.height / 2);
        if (rotation === 2) this._debugCtx.rotate(90 * Math.PI / 180);
        else if (rotation === 3) this._debugCtx.rotate(-90 * Math.PI / 180);
        else if (rotation === 4) this._debugCtx.rotate(180 * Math.PI / 180);
        this._debugCtx.drawImage(snapshot, -sw * scale / 2, -sh * scale / 2, sw * scale, sh * scale);
        this._debugCtx.restore();
      } else {
        scale = Math.min(1, maxSize / Math.max(sw, sh));
        this._debugCanvas.width = Math.round(sw * scale);
        this._debugCanvas.height = Math.round(sh * scale);
        this._debugCtx.drawImage(snapshot, 0, 0, this._debugCanvas.width, this._debugCanvas.height);
      }

      this.debugImage = this._debugCanvas.toDataURL('image/jpeg', 0.7);
      this._debugImg.src = this.debugImage;
      // 防御：读点也确保 _detectCount 为有效数字
      if (typeof this._detectCount !== 'number' || isNaN(this._detectCount)) {
        this._detectCount = 0;
      }
      var originalSize = originalSnapshot
        ? (originalSnapshot.width + 'x' + originalSnapshot.height)
        : (sw + 'x' + sh);
      this._debugLabel.textContent =
        '设备旋转=' + (sourceRotation || rotation) +
        ' | 原始帧 ' + originalSize +
        ' → 模型输入 ' + sw + 'x' + sh +
        ' | 预览 ' + this._debugCanvas.width + 'x' + this._debugCanvas.height +
        ' | #' + this._detectCount;
    },

    // ===================== 模型状态指示灯（可插拔） =====================

    /**
     * 创建右下角状态指示灯
     * 动态注入 DOM，不依赖模板，插拔无痕
     */
    _createModelDot() {
      if (this._modelDotEl) return;
      var self = this;

      // 容器
      var dot = document.createElement('div');
      dot.style.cssText =
        'position:fixed;bottom:0.2rem;right:0.2rem;z-index:9999;' +
        'width:0.24rem;height:0.24rem;border-radius:50%;' +
        'box-shadow:0 0 0.08rem rgba(0,0,0,0.3);' +
        'transition:background-color 0.3s;' +
        'background-color:#f00;';
      dot.title = '模型未加载';
      this._modelDotEl = dot;

      // 挂载
      this.$nextTick(function () {
        document.body.appendChild(self._modelDotEl);
      });

      // 轮询 sessionStorage 中的模型状态
      this.modelDotTimer = setInterval(function () {
        try {
          var raw = sessionStorage.getItem('__modelStatus');
          if (raw !== null) {
            self.modelStatus = parseInt(raw, 10) || 0;
            self._modelDotEl.style.backgroundColor = self.modelDotColor;
            self._modelDotEl.title = self.modelDotText;
          }
        } catch (e) { /* sessionStorage 不可用时静默忽略 */ }
      }, 500);
    },

    /**
     * 移除状态指示灯
     */
    _removeModelDot() {
      if (this.modelDotTimer) {
        clearInterval(this.modelDotTimer);
        this.modelDotTimer = null;
      }
      if (this._modelDotEl && this._modelDotEl.parentNode) {
        this._modelDotEl.parentNode.removeChild(this._modelDotEl);
      }
      this._modelDotEl = null;
    },

    /**
     * 更新调试结果标签：显示分类分数、距离状态等
     * @param {Object} result - detect() 返回的 { status, label, scores }
     */
    _updateDebugResult(result) {
      if (!this._debugResultLabel) return;

      var status = typeof result.status === 'number' ? result.status : STATUS.NOT_DETECTED;
      var label = result.label || '';
      var scores = result.scores;
      var lines = [];

      // 状态行
      lines.push('检测状态：' + (STATUS_TEXT[status] || '未知状态'));
      if (this.needTongueDetect) {
        lines.push('分类结果：' + (label || '未识别'));
      } else if (label) {
        lines.push('识别结果：' + label);
      }

      // 分类分数（嘴/暗/正常）
      if (scores) {
        lines.push(
          '嘴:' + (scores['嘴'] * 100).toFixed(0) + '% ' +
          '暗:' + (scores['暗'] * 100).toFixed(0) + '% ' +
          '正常:' + (scores['正常'] * 100).toFixed(0) + '%'
        );
      }

      // 距离提示
      if (status === STATUS.NOT_DETECTED) lines.push('请将完整面部放入画面中');
      else if (status === STATUS.TOO_FAR) lines.push('请靠近摄像头一些');
      else if (status === STATUS.TOO_CLOSE) lines.push('请远离摄像头一些');

      this._debugResultLabel.textContent = lines.join('\n');
    },

    /**
     * 处理检测结果，更新 UI 提示
     */
    applyDetectionResult(result) {
      var status = typeof result.status === 'number' ? result.status : STATUS.NOT_DETECTED;
      var label = result.label || '';
      var passed = this.needFaceDetect
        ? status === STATUS.OK
        : status === STATUS.OK && label === this.passLabel;

      this.detectStatus = status;
      this.detectPassed = passed;

      // 防御：确保计数器为有效数字，避免首次通过时出现 NaN。
      if (typeof this._detectCount !== 'number' || isNaN(this._detectCount)) {
        this._detectCount = 0;
      }
      if (typeof this._passCount !== 'number' || isNaN(this._passCount)) {
        this._passCount = 0;
      }
      console.log('[Detector] #' + this._detectCount +
        ' status=' + status + '(' + (STATUS_NAME[status] || '-') + ')' +
        ' label=' + (label || '-') +
        ' expect=' + (this.passLabel || '-') +
        ' passed=' + passed +
        ' passCount=' + this._passCount);

      if (passed) {
        this._passCount++;
        if (this._passCount >= this.requiredPassCount) {
          // 连续通过达到要求 → 自动拍照
          this.detectTip = '正在自动拍照...';
          this._autoCaptureDone = true;
          this.photograph();
          return;
        }
        this.detectTip = '检测通过(' + this._passCount + '/' + this.requiredPassCount + ')，请保持当前姿势...';
        return;
      }

      // 未通过则重置连续计数
      this._passCount = 0;

      switch (status) {
        case STATUS.NOT_DETECTED:
          this.detectTip = this.needTongueDetect ? '请伸出舌头对准摄像头' : '请将面部对准摄像头';
          break;
        case STATUS.TOO_FAR:
          this.detectTip = this.needTongueDetect ? '距离太远，请靠近摄像头' : '面部距离太远，请靠近摄像头';
          break;
        case STATUS.TOO_CLOSE:
          this.detectTip = this.needTongueDetect ? '距离太近，请远离摄像头' : '面部距离太近，请远离摄像头';
          break;
        case STATUS.OK:
          if (label === '暗') {
            this.detectTip = '光线太暗，请调整光线';
          } else {
            this.detectTip = this.needTongueDetect ? '请伸出舌头对准摄像头' : '请将面部对准摄像头';
          }
          break;
      }
    },
  },
};
